"use client";

import { useSessionWorkspace } from "./org_provider";
import { buildMarketingURL } from "@/utils/helpers";
import { OrganizationId } from "@/utils/types";
import { Info } from "lucide-react";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useCookies } from "react-cookie";

type BannerId = `self-serve-banner-${OrganizationId}`;

export type BannerLocation = "dashboard" | "study";

type Banner = {
  id: BannerId;
  message: ReactNode;
  locations: BannerLocation[];
};

const getSelfServeBanner = (workspaceId: OrganizationId): Banner => {
  return {
    id: `self-serve-banner-${workspaceId}`,
    message: (
      <p className="flex items-center gap-1">
        <Info className="w-4 h-4" />
        <span className="font-medium">
          Welcome to Listen Labs! For a hands-on experience
        </span>
        <a href={buildMarketingURL({ path: "/book-a-demo" })} target="_blank">
          talk to our research experts
        </a>
      </p>
    ),
    locations: ["dashboard"],
  };
};

const RELEASE_DATE = new Date("2025-11-03");
const BANNER_VISIBILITY_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

const DISMISSED_BANNERS_COOKIE = "dismissed_banners";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

type BannerProviderContext = {
  banners: Banner[];
  removeBanner: (id: BannerId) => void;
};

const Context = createContext<BannerProviderContext | undefined>(undefined);

export default function BannerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { org: workspace } = useSessionWorkspace();
  const [cookies, setCookie] = useCookies([DISMISSED_BANNERS_COOKIE]);
  const banners = useMemo(() => {
    return !workspace ? [] : [getSelfServeBanner(workspace.id)];
  }, [workspace]);

  const dismissedBanners = useMemo(() => {
    const dismissed = cookies[DISMISSED_BANNERS_COOKIE] as string[];
    if (Array.isArray(dismissed)) {
      return new Set(
        dismissed.filter((id): id is string => typeof id === "string")
      );
    }
    return new Set<string>();
  }, [cookies]);

  const removeBanner = useCallback(
    (id: BannerId) => {
      const newDismissed = Array.from(new Set([...dismissedBanners, id]));
      setCookie(DISMISSED_BANNERS_COOKIE, newDismissed, {
        path: "/",
        maxAge: COOKIE_MAX_AGE,
        sameSite: "lax",
      });
    },
    [dismissedBanners, setCookie]
  );
  const filteredBanners = useMemo(() => {
    const shouldShowSelfServeBanner = (banner: Banner) =>
      workspace?.org_type &&
      workspace.org_type === "self_serve" &&
      banner.id.startsWith("self-serve-banner-") &&
      new Date(workspace.created_at) > RELEASE_DATE &&
      Date.now() - new Date(workspace.created_at).getTime() <
        BANNER_VISIBILITY_DURATION_MS;

    const shouldShowBanner = (banner: Banner) => {
      return shouldShowSelfServeBanner(banner);
    };

    return banners.filter(
      (banner) => !dismissedBanners.has(banner.id) && shouldShowBanner(banner)
    );
  }, [banners, dismissedBanners, workspace]);

  return (
    <Context.Provider
      value={{
        banners: filteredBanners,
        removeBanner,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export const useBanners = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error("useUser must be used inside UserProvider");
  }
  return context;
};
